import { useCallback, useState } from 'react';
import { FormActionType } from 'framework/ui/menu';
import {
  CloseFormShowButton,
  Dropdown,
  EditFormShowButton,
  FormFieldsData,
  SaveFormShowButton,
  TextArea,
  TextInput,
  useFormShowContext,
} from 'framework/form';
import {
  EDITABLE_LIST_COLLAPSIBLE_ROW,
  EDITABLE_LIST_CUSTOM,
  EDITABLE_LIST_FIELD,
  EDITABLE_LIST_GROUP,
  EditableListDataGridLayout,
  Filters,
  FiltersLayout,
  NEW_ROW_ID_PREFIX,
} from 'framework/list';
import { Tooltip } from 'framework/ui/mui-builder';
import { useLocation } from 'react-router-dom';
import { defaultsDeep } from 'lodash';
import { AnchorField, FormattedTextField, getAnchorRowStyle } from 'framework/ui/field';
import { MILESTONES_ALIAS } from './useMilestonesService';
import OriginalPlannedDateInput from './OriginalPlannedDateInput';
import CurrentlyPlannedDateInput from './CurrentlyPlannedDateInput';
import { type Data } from 'framework/types';
import OriginalPlannedDateField from './OriginalPlannedDateField';
import CurrentlyPlannedDateField from './CurrentlyPlannedDateField';
import LinkedMilestonesField from '../businessOutcomes/LinkedMilestonesField';
import MilestonesModalsActionButton from './MilestonesModalsActionButton';
import { EMPTY_VALUE_INDICATOR } from 'framework/utils';
import MilestonesModalsManager from './MilestonesModalsManager';
import { useShowContext } from 'framework/show';
import { Box, Grid2 as Grid, SxProps, Typography, TypographyProps } from '@mui/material';
import { isProtectedMilestone } from './utils/milestoneProtection';
import { MilestonesType } from './types';
import ListContainsFilter from '../shared/filters/ListContainsFilter';
import { BusinessOutcomesType } from '../businessOutcomes/types';
import {
  Milestone,
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  MILESTONE_STATUS,
  MILESTONE_STATUS_OPTIONS,
  OUTCOME_STATUS,
  useProjectSecurityContext,
  StatusFilter,
  ProjectSectionAccordion,
  ListSortButton,
} from 'domain/shared/newProject';
import { compactDisabledInputSx } from 'domain/projects/newProject/shared/compactDisabledInputSx';

type MilestonesProps = {
  isMilestoneBillingType: boolean;
  isChangeReason?: boolean;
  isRejectionReason?: (milestone: Milestone) => boolean;
  businessOutcomes?: { alias: string; name: string; status: string }[];
  accordionSx?: SxProps;
  titleSx?: SxProps;
  titleVariant?: TypographyProps['variant'];
  gridSx?: SxProps;
};

const Milestones = ({
  isMilestoneBillingType,
  businessOutcomes,
  isRejectionReason,
  isChangeReason,
  titleSx,
  titleVariant = 'h4',
  accordionSx,
  gridSx = { pl: 2 },
}: MilestonesProps) => {
  const { isEditMode } = useFormShowContext();
  const { data: projectData } = useShowContext();
  const { isAccessGranted } = useProjectSecurityContext();
  const { hash } = useLocation();
  const [modalSignal, setModalSignal] = useState<FormActionType | null>(null);

  const options = businessOutcomes?.map(({ alias, name }) => ({
    value: alias,
    label: name.length > 0 ? `${alias} – ${name}` : alias,
  }));

  const renderActions = useCallback(() => {
    if (isAccessGranted(ACCESS_INFO_SECTIONS.MILESTONE, ACCESS_INFO_ACTIONS.MANAGE)) {
      return isEditMode
        ? [<SaveFormShowButton key="save" />, <CloseFormShowButton key="close" />]
        : [<EditFormShowButton key="edit" />];
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  const filters = (
    <Filters>
      <FiltersLayout>
        <StatusFilter source="status" options={MILESTONE_STATUS_OPTIONS} label={'Status'} />
        <ListContainsFilter source="businessOutcomes" label="Business outcomes" options={options} />
      </FiltersLayout>
    </Filters>
  );

  return (
    <ProjectSectionAccordion
      defaultExpanded
      sx={accordionSx}
      title={
        <Grid
          container
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center', width: '100%', ...gridSx }}
        >
          <Grid size={2.5}>
            <Typography variant={titleVariant} color="primary" sx={titleSx}>
              Milestones
            </Typography>
          </Grid>
          <Grid size={0.5}>
            <ListSortButton
              buttonId="milestones-sort-button"
              options={[
                { label: 'Original planned date', source: 'initialPlannedDate' },
                { label: 'Currently planned date', source: 'currentPlannedDate' },
              ]}
            />
          </Grid>
          <Grid
            size="auto"
            sx={{
              '.add-filter-button__wrapper': { mb: 0 },
              '.filter-item-wrapper': { mb: 0 },
              '.filters-reset-btn': { mb: 0 },
            }}
          >
            {!isEditMode ? filters : <></>}
          </Grid>
          <Grid size="grow" />
          <Grid size="auto" display="flex" alignItems="center">
            {renderActions()}
          </Grid>
        </Grid>
      }
    >
      <EditableListDataGridLayout
        rowProps={data =>
          defaultsDeep(getAnchorRowStyle(data, hash), {
            sx: {
              alignItems: isEditMode ? 'flex-start' : 'center',
              py: isEditMode ? 2 : 0,
            },
          })
        }
        rerenderTableCellsDeps={businessOutcomes}
        getRowEnabled={data => !data.status || data.status === MILESTONE_STATUS.ACTIVE}
        disabledRemove={data => isProtectedMilestone(data as MilestonesType)}
        newRowDefaultData={{ type: isMilestoneBillingType ? null : MILESTONES_ALIAS.NON_BILLING_MILESTONE }}
        getTableCells={async isEditModeLayout => {
          if (isEditModeLayout) {
            return Promise.resolve([
              {
                type: EDITABLE_LIST_FIELD,
                source: 'alias',
                isEditor: true,
                component: TextInput,
                componentProps: {
                  label: '',
                  disabled: true,
                },
                gridCellProps: {
                  size: 0.5,
                  sx: compactDisabledInputSx,
                },
              },
              {
                type: EDITABLE_LIST_GROUP,
                component: Grid,
                componentProps: {
                  container: true,
                  alignItems: 'start',
                  spacing: 1,
                },
                gridCellProps: {
                  size: 11,
                  sx: {
                    '.floating-label-input': { mb: '0px!important' },
                    '.floating-label-baseInput': { mb: '0px!important' },
                    '.MuiFormControl-root': { mb: '0px!important' },
                  },
                },
                children: [
                  {
                    type: EDITABLE_LIST_FIELD,
                    source: 'title',
                    isEditor: true,
                    component: TextInput,
                    componentProps: {
                      label: 'Milestone',
                      sx: { '.floating-label-input': { mb: '0px!important' } },
                    },
                    gridCellProps: { size: 5 },
                  },
                  {
                    type: EDITABLE_LIST_FIELD,
                    source: 'initialPlannedDate',
                    isEditor: true,
                    component: OriginalPlannedDateInput,
                    componentProps: {
                      label: 'Original planned date',
                      sx: { '.MuiFormControl-root': { mb: '0px!important' }, mb: 0 },
                      disabled: (data?: FormFieldsData) => {
                        const isNewRow = data?.id?.toString().startsWith(NEW_ROW_ID_PREFIX);
                        if (isNewRow) return false;

                        return (
                          !!data?.hasInitialPlannedDateFromBackend ||
                          data?.status === MILESTONE_STATUS.COMPLETED ||
                          data?.status === MILESTONE_STATUS.REJECTED
                        );
                      },
                    },
                    gridCellProps: { size: 3.5 },
                  },
                  {
                    type: EDITABLE_LIST_FIELD,
                    source: 'currentPlannedDate',
                    isEditor: true,
                    component: CurrentlyPlannedDateInput,
                    componentProps: {
                      label: 'Currently planned date',
                      sx: { '.MuiFormControl-root': { mb: '0px!important' }, mb: 0 },
                    },
                    gridCellProps: { size: 3.5 },
                  },

                  {
                    type: EDITABLE_LIST_FIELD,
                    source: 'description',
                    isEditor: true,
                    component: TextArea,
                    componentProps: {
                      rows: 1,
                      label: 'Description',
                      sx: { '.floating-label-input': { mb: '0px!important' } },
                    },
                    gridCellProps: { size: 5 },
                  },
                  {
                    type: EDITABLE_LIST_FIELD,
                    source: 'businessOutcomes',
                    isEditor: true,
                    component: Dropdown,
                    componentProps: {
                      options,
                      multiple: true,
                      placeholder: 'Select related outcomes(s)',
                      label: '',
                      sx: { '.floating-label-input': { mb: '0px!important' }, mb: 0 },
                    },
                    gridCellProps: {
                      size: 3.5,
                    },
                  },
                  {
                    type: EDITABLE_LIST_FIELD,
                    source: 'comment',
                    isEditor: true,
                    isVisible: (data: Data) => !!data.currentPlannedDate,
                    component: TextInput,
                    componentProps: {
                      label: 'Change date reason',
                      sx: { '.floating-label-input': { mb: '0px!important' } },
                    },
                    gridCellProps: { size: 3.5 },
                  },
                  ...(isRejectionReason
                    ? [
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'rejectionReason',
                          isEditor: true,
                          component: TextInput,
                          isVisible: (data: Data) => isRejectionReason(data as Milestone),
                          componentProps: {
                            label: 'Rejection reason',
                            sx: { '.floating-label-input': { mb: '0px!important' } },
                          },
                          gridCellProps: { size: 5 },
                        },
                      ]
                    : []),
                ],
              },
            ]);
          }
          return Promise.resolve([
            {
              type: EDITABLE_LIST_COLLAPSIBLE_ROW,
              gridCellProps: { size: 12 },
              title: [
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'alias',
                  isEditor: false,
                  component: AnchorField,
                  componentProps: {
                    isInternal: true,
                    label: '',
                  },
                  gridCellProps: {
                    size: 0.5,
                    justifyContent: 'center',
                    display: 'flex',
                  },
                },
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'title',
                  isEditor: false,
                  component: FormattedTextField,
                  componentProps: {
                    label: '',
                    format: (str: string) =>
                      str && str.length > 60 ? (
                        <Tooltip title={str}>
                          <div>
                            <Typography
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: 1.2,
                                wordBreak: 'break-word',
                              }}
                            >
                              {str}
                            </Typography>
                          </div>
                        </Tooltip>
                      ) : (
                        str
                      ),
                  },
                  gridCellProps: { size: 3.5 },
                },
                {
                  type: EDITABLE_LIST_CUSTOM,
                  isEditor: false,
                  component: OriginalPlannedDateField,
                  componentProps: {
                    initialPlannedDateSource: 'initialPlannedDate',
                    statusSource: 'status',
                    label: '',
                    color: 'error.main',
                  },
                  gridCellProps: { size: 2 },
                },
                {
                  type: EDITABLE_LIST_CUSTOM,
                  component: CurrentlyPlannedDateField,
                  componentProps: {
                    currentPlannedDateSource: 'currentPlannedDate',
                    completionDateSource: 'completionDate',
                    rejectionDateSource: 'rejectionDate',
                  },
                  gridCellProps: { size: 2 },
                },
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'businessOutcomes',
                  isEditor: false,
                  component: LinkedMilestonesField,
                  componentProps: {
                    label: '',
                    options: businessOutcomes,
                    isCompletedFn: (businessOutcome: BusinessOutcomesType) =>
                      businessOutcome?.status === OUTCOME_STATUS.COMPLETED,
                  },
                  gridCellProps: {
                    size: isAccessGranted(ACCESS_INFO_SECTIONS.MILESTONE, ACCESS_INFO_ACTIONS.MANAGE) ? 2.5 : 3,
                  },
                },
                {
                  type: EDITABLE_LIST_CUSTOM,
                  component: MilestonesModalsActionButton,
                  componentProps: {
                    projectId: projectData.id,
                    onClick: setModalSignal,
                  },
                  gridCellProps: {
                    size: isAccessGranted(ACCESS_INFO_SECTIONS.MILESTONE, ACCESS_INFO_ACTIONS.MANAGE) ? 1.5 : 0.5,
                    justifyContent: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    height: '36px',
                  },
                },
              ],
              details: [
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'description',
                  isEditor: false,
                  component: FormattedTextField,
                  componentProps: {
                    label: '',
                    format: (str: string) => (
                      <Box
                        pb={isChangeReason || isRejectionReason ? 1 : 0}
                        pl={'6px'}
                        sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}
                      >
                        {str ? str : EMPTY_VALUE_INDICATOR}
                      </Box>
                    ),
                  },
                  gridCellProps: { size: 12 },
                },
                ...(isChangeReason
                  ? [
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'comment',
                        isEditor: false,
                        component: FormattedTextField,
                        componentProps: {
                          label: '',
                          format: (str: string) =>
                            str ? (
                              <Box pb={1} pl={'6px'}>
                                <Typography color="primary" variant="subtitle1">
                                  Change reason:
                                </Typography>
                                {str}
                              </Box>
                            ) : (
                              ' '
                            ),
                        },
                        gridCellProps: {
                          size: 12,
                        },
                      },
                    ]
                  : []),
                ...(isRejectionReason
                  ? [
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'rejectionReason',
                        isEditor: false,
                        component: FormattedTextField,
                        componentProps: {
                          label: '',
                          format: (str: string) =>
                            str ? (
                              <Box pb={1} pl={'6px'}>
                                <Typography color="primary" variant="subtitle1">
                                  Rejection reason:
                                </Typography>

                                {str}
                              </Box>
                            ) : (
                              ' '
                            ),
                        },
                        gridCellProps: {
                          size: 12,
                          pb: 1,
                        },
                      },
                    ]
                  : []),
              ],
            },
          ]);
        }}
      />
      <MilestonesModalsManager
        signal={modalSignal}
        onClose={() => {
          setModalSignal(null);
        }}
      />
    </ProjectSectionAccordion>
  );
};
export default Milestones;
